import { useState } from 'react'
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent,
  DialogTitle, Snackbar, TextField, Tooltip, Typography,
} from '@mui/material'
import { Copy, Plus } from 'lucide-react'
import { AppShell, Crumb } from '@agribot/polygon-checks-ui'
import { usePrincipal } from '@agribot/polygon-checks-ui'
import {
  useProvisionUser, useProvisionedUsers, useRevokeProvisionedUser, useSetUserNote,
} from './hooks'
import { EmptyState, Eyebrow, Mono, StatusPill, Surface } from '@agribot/polygon-checks-ui'
import { SkeletonGridRows } from '@agribot/polygon-checks-ui'
import type { ProvisionedUser } from './types'
import { formatDate, num } from '@agribot/polygon-checks-ui'
import { tokens } from '@agribot/polygon-checks-ui'

const GRID = 'minmax(0,1.2fr) minmax(0,1.1fr) minmax(0,0.9fr) 100px 100px 100px'

/**
 * A partner managing their own users.
 *
 * This is what a root key is for, and until now it could only be done with
 * curl: the key is a valid credential that reached no screen at all. It
 * reaches this one, and its own usage, and nothing else — every other part
 * of the application would answer 403, so the shell does not offer them.
 *
 * A user here is a uuid. The partner holds the mapping to whoever is behind
 * it; we hold nothing that identifies anyone.
 */
export function PartnerUsers() {
  const { data: principal } = usePrincipal()
  const { data, isLoading } = useProvisionedUsers()
  const provision = useProvisionUser()
  const revoke = useRevokeProvisionedUser()

  const [minted, setMinted] = useState<{ id: string; token: string } | null>(null)
  const [naming, setNaming] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const users = data?.users ?? []
  const key = principal?.key

  const actions = (
    <Button
      size="small"
      startIcon={<Plus size={14} strokeWidth={1.5} />}
      disabled={provision.isPending}
      onClick={() => setNaming(true)}
    >
      New user
    </Button>
  )

  return (
    <AppShell
      crumbs={[<Crumb key="a">{key?.organisation_name ?? 'Admin'}</Crumb>, <Crumb key="b">Users</Crumb>]}
      actions={actions}
    >
      <Box sx={{ flex: 1, overflowY: 'auto', px: 8, py: 7 }}>
        <Eyebrow sx={{ mb: 2 }}>Admin</Eyebrow>
        <Typography variant="h1" sx={{ mb: 1 }}>Users</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 5, maxWidth: 680 }}>
          Each user is a reference and a long-lived token — no email, no name,
          no password. Map the reference to whoever it is in your own system;
          we hold nothing that identifies anyone. A new user sees nothing
          until somebody who already holds a region grants it.
        </Typography>

        <Surface sx={{ overflow: 'hidden' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: GRID, columnGap: 3, px: 5 }}>
            {['User', 'Note', 'Created by', 'Regions', 'Token', ''].map((h, i) => (
              <Box
                key={h || i}
                sx={{
                  display: 'flex', alignItems: 'center', height: 36,
                  justifyContent: i >= 3 && i <= 4 ? 'flex-end' : 'flex-start',
                  font: `500 11px ${tokens.font.sans}`, letterSpacing: '0.08em',
                  textTransform: 'uppercase', color: 'text.disabled',
                }}
              >
                {h}
              </Box>
            ))}
            {isLoading && <SkeletonGridRows columns={6} rows={4} align={[3, 4]} />}
            {users.map((u) => (
              <UserRow
                key={u.id}
                row={u}
                onRevoke={() =>
                  revoke.mutate(u.id, {
                    onSuccess: (r) => setMinted({ id: u.id, token: r.token }),
                    onError: (e) => setError((e as Error).message),
                  })}
              />
            ))}
          </Box>

          {!isLoading && users.length === 0 && (
            <EmptyState
              title="No users yet"
              body="Create one and hand its token to whatever will be calling the API."
              action={actions}
            />
          )}
        </Surface>
      </Box>

      <NewUserDialog
        open={naming}
        pending={provision.isPending}
        onClose={() => setNaming(false)}
        onCreate={(note) =>
          provision.mutate(note, {
            onSuccess: (u) => { setNaming(false); setMinted({ id: u.id, token: u.token }) },
            onError: (e) => setError((e as Error).message),
          })}
      />
      <TokenDialog minted={minted} onClose={() => setMinted(null)} />
      <Snackbar open={Boolean(error)} autoHideDuration={8000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </AppShell>
  )
}

function UserRow({ row, onRevoke }: { row: ProvisionedUser; onRevoke: () => void }) {
  const setNote = useSetUserNote()
  const cell = {
    height: 48, display: 'flex', alignItems: 'center', minWidth: 0,
    borderTop: `1px solid ${tokens.color.ink200}`,
  }
  const right = (node: React.ReactNode) => (
    <Box sx={{ ...cell, justifyContent: 'flex-end', fontVariantNumeric: 'tabular-nums' }}>{node}</Box>
  )

  return (
    <Box sx={{ display: 'contents', '&:hover > *': { backgroundColor: tokens.color.ink50 } }}>
      <Box sx={{ ...cell, minWidth: 0 }}>
        <Box sx={{ minWidth: 0 }}>
          {/* In full: this is the reference the partner matches on. */}
          <Mono sx={{ fontSize: 12 }}>{row.id}</Mono>
          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
            created {formatDate(row.created_at)}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ ...cell, minWidth: 0 }}>
        {/* Editable in place: a note earns its keep by being corrected when
            whatever it describes changes hands. */}
        <TextField
          fullWidth
          variant="standard"
          defaultValue={row.note ?? ''}
          placeholder="add a note"
          disabled={setNote.isPending}
          onBlur={(e) => {
            const next = e.target.value.trim() || null
            if (next !== (row.note ?? null)) setNote.mutate({ id: row.id, note: next })
          }}
          inputProps={{ 'aria-label': `Note for ${row.id}`, maxLength: 200 }}
          sx={{ '& input': { font: `13px ${tokens.font.sans}`, py: 0.5 } }}
        />
      </Box>
      <Box sx={{ ...cell, gap: 2, minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
          {row.created_by_key.name}
        </Typography>
        {row.created_by_key.revoked && <StatusPill status="rejected" label="key revoked" />}
      </Box>
      {right(
        row.regions === 0
          ? <Typography variant="body2" sx={{ color: 'text.disabled' }}>none</Typography>
          : <Typography variant="body2">{num(row.regions)}</Typography>,
      )}
      {right(
        row.token_version > 1
          ? <StatusPill status="flagged" label={`v${row.token_version}`} />
          : <Typography variant="caption" sx={{ color: 'text.disabled' }}>original</Typography>,
      )}
      <Box sx={{ ...cell, justifyContent: 'flex-end' }}>
        <Tooltip title="Invalidates every token already issued to this user and returns a fresh one">
          <Button size="small" variant="text" color="error" onClick={onRevoke}>
            Revoke
          </Button>
        </Tooltip>
      </Box>
    </Box>
  )
}

/**
 * Naming it before minting it.
 *
 * A list of uuids tells a partner nothing about which integration each one
 * belongs to. The note is optional and stays optional — the user is a uuid
 * whether or not anyone labels it.
 */
function NewUserDialog({
  open, pending, onClose, onCreate,
}: {
  open: boolean
  pending: boolean
  onClose: () => void
  onCreate: (note: string | undefined) => void
}) {
  const [note, setNote] = useState('')
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>New user</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          A reference and a token. The note is for your records — which
          integration or team this one is for.
        </Typography>
        <TextField
          fullWidth
          size="small"
          autoFocus
          value={note}
          placeholder="nightly import job, owned by the data team"
          onChange={(e) => setNote(e.target.value)}
          inputProps={{ 'aria-label': 'Note', maxLength: 200 }}
        />
        <Typography variant="caption" sx={{ color: 'text.disabled', mt: 2, display: 'block' }}>
          We never read this and it is not a name field. A user is a uuid here
          precisely so that we hold nothing identifying anyone — whatever you
          put here is yours to keep that way.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>Cancel</Button>
        <Button disabled={pending} onClick={() => onCreate(note.trim() || undefined)}>
          {pending ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * The token, once.
 *
 * Nothing stores it, so there is no route that could return it again — the
 * same bargain the key itself was handed under.
 */
function TokenDialog(
  { minted, onClose }: { minted: { id: string; token: string } | null; onClose: () => void },
) {
  const [copied, setCopied] = useState(false)
  return (
    <Dialog
      open={Boolean(minted)}
      onClose={() => { setCopied(false); onClose() }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>User {minted?.id.slice(0, 8)}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          Copy this now — nothing stores it, so it cannot be shown again.
          Send it as <code>Authorization: Bearer …</code>, or paste it into
          the sign-in screen to see the application as this user.
        </Typography>
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 2, p: 3,
            borderRadius: `${tokens.radius.md}px`, backgroundColor: tokens.color.ink100,
          }}
        >
          <Mono sx={{ fontSize: 12, flex: 1, wordBreak: 'break-all' }}>{minted?.token}</Mono>
          <Tooltip title={copied ? 'Copied' : 'Copy'}>
            <Button
              size="small"
              variant="text"
              sx={{ flex: 'none' }}
              onClick={async () => {
                if (minted) await navigator.clipboard.writeText(minted.token)
                setCopied(true)
              }}
            >
              <Copy size={16} strokeWidth={1.5} />
            </Button>
          </Tooltip>
        </Box>
        <Typography variant="caption" sx={{ color: 'text.disabled', mt: 2, display: 'block' }}>
          This user can see nothing until a region is granted to{' '}
          <Mono sx={{ fontSize: 11 }}>{minted?.id}</Mono>.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { setCopied(false); onClose() }}>Done</Button>
      </DialogActions>
    </Dialog>
  )
}
